import React, { useState } from 'react';
import { ProfileFormProps, StaffUser } from '../../../types';
import { FormField, ProfileAvatar } from '../atoms';
import Button from '../../atoms/Button';
import './ProfileForm.css';

const ProfileForm: React.FC<ProfileFormProps> = ({
  user,
  onUpdate,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    firstName: user.firstName,
    lastName: user.lastName,
    displayName: user.displayName,
    email: user.email,
    phoneNumber: user.phoneNumber || ''
  });
  
  const [photoURL, setPhotoURL] = useState(user.photoURL);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value
    });
    
    // Clear error for this field
    if (errors[id]) {
      setErrors({
        ...errors,
        [id]: ''
      });
    }
  };
  
  const handlePhotoUpload = (file: File) => {
    // Create a blob URL for preview
    const previewURL = URL.createObjectURL(file);
    setPhotoURL(previewURL);
  };
  
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    if (formData.phoneNumber && !/^\+?[0-9\s-()]{10,15}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Invalid phone number format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Combine display name from first and last name
    const displayName = `${formData.firstName} ${formData.lastName}`.trim();
    
    const updatedUser: Partial<StaffUser> = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      displayName,
      email: formData.email,
      phoneNumber: formData.phoneNumber || undefined,
      photoURL
    };
    
    onUpdate(updatedUser);
  };
  
  return (
    <form className="profile-form" onSubmit={handleSubmit}>
      <div className="profile-header">
        <ProfileAvatar
          photoURL={photoURL}
          displayName={formData.displayName}
          size="large"
          onUpload={handlePhotoUpload}
          className="profile-avatar-large"
        />
        <div className="profile-title">
          <h3>{formData.displayName || 'Your Profile'}</h3>
          <p className="profile-role">{user.role.charAt(0).toUpperCase() + user.role.slice(1)}</p>
        </div>
      </div>
      
      <div className="profile-form-fields">
        <div className="form-row">
          <FormField
            id="firstName"
            label="First Name"
            value={formData.firstName}
            onChange={handleInputChange}
            required
            error={errors.firstName}
          />
          
          <FormField
            id="lastName"
            label="Last Name"
            value={formData.lastName}
            onChange={handleInputChange}
            required
            error={errors.lastName}
          />
        </div>
        
        <FormField
          id="email"
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          required
          error={errors.email}
        />
        
        <FormField
          id="phoneNumber"
          label="Phone Number"
          type="tel"
          value={formData.phoneNumber}
          onChange={handleInputChange}
          placeholder="+1 (555) 123-4567"
          error={errors.phoneNumber}
        />
      </div>
      
      <div className="profile-form-actions">
        <Button
          type="submit"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
};

export default ProfileForm;
